import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import SkillsInput from '../components/SkillsInput';
import {
  Search as SearchIcon, Briefcase, Zap, X,
  Clock, CheckCircle, Filter, User, Users, Linkedin, MessageSquare
} from 'lucide-react';

// CALENDAR IMPORTS
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import enGbLocale from '@fullcalendar/core/locales/en-gb';

import { INDUSTRY_DOMAINS } from '../constants/domains';
import { RATE_CARD, calculateTotalHours } from '../utils/engagementUtils';

import './DiscoverProfessorsUni.css';

const NotificationModal = ({ message, type, onClose }) => {
  if (!message) return null;
  return (
    <div className="unidis-notification-overlay">
      <div className="unidis-notification-card">
        <div className={`unidis-notif-header ${type === 'success' ? 'unidis-notif-success' : 'unidis-notif-error'}`}>
          {type === 'success' ? <CheckCircle size={28} /> : <Zap size={28} className="transform rotate-12" />}
          <h3 className="unidis-notif-title">
            {type === 'success' ? 'Success' : 'Attention Needed'}
          </h3>
        </div>
        <p className="unidis-notif-msg">
          {message}
        </p>
        <div className="unidis-notif-actions">
          <button
            onClick={onClose}
            className={`unidis-notif-btn ${type === 'success' ? 'btn-success' : 'btn-error'}`}
          >
            Acknowledge
          </button>
        </div>
      </div>
    </div>
  );
};

function DiscoverProfessorsUni() {
  const navigate = useNavigate();
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);

  // University Profile for tier-based pricing
  const [uniProfile, setUniProfile] = useState(null);

  // Selection & Modal States
  const [selectedProf, setSelectedProf] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [busySlots, setBusySlots] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profFeedback, setProfFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  // Notification State
  const [notification, setNotification] = useState(null); // { message, type }

  // Proposal Form State
  const [sessionForm, setSessionForm] = useState({
    title: '',
    description: '',
    domain: '',
    engagement_type: 'MODULAR',
    delivery_mode: 'Remote',
    event_date: '',
    start_time: '',
    end_time: '',
    duration_value: 1,
    // Dynamic Fields
    days_of_week: [],
    end_date: '',
    weekly_hours: 0
  });

  const [filters, setFilters] = useState({
    nameSearch: '',
    domainSearch: '',
    domain: [],
    skills: [],
    minExperience: 0,
    minCredibility: 0,
    roleFilter: 'ALL'
  });

  const availableDomains = INDUSTRY_DOMAINS;
  const commonSkills = ["Python", "Machine Learning", "Data Science", "React", "Cybersecurity"];

  // 1. Fetch Professors List
  useEffect(() => {
    const fetchProfs = async () => {
      setLoading(true);
      try {
        const finalDomains = [...filters.domain];
        // domainSearch is for UI filtering only

        // Simple param construction
        const params = new URLSearchParams();
        if (finalDomains.length > 0) params.append('domain', finalDomains.join(','));
        if (filters.skills.length > 0) params.append('skills', filters.skills.join(','));
        if (filters.minExperience > 0) params.append('min_experience', filters.minExperience);
        if (filters.minCredibility > 0) params.append('min_credibility', filters.minCredibility);

        // If no filters, might want to fetch all or random
        const res = await api.get(`/professors/discover?${params.toString()}`);
        setProfessors(res.data || []);
      } catch (err) {
        console.error("Discovery error:", err);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchProfs, 300);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Fetch university profile for tier-based pricing
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/profile/me');
        setUniProfile(res.data);
      } catch (err) {
        console.error("Failed to fetch profile", err);
      }
    };
    fetchProfile();
  }, []);

  // Calculate estimated budget using shared utility
  const calculateBudget = () => {
    const currentTier = uniProfile?.tier?.trim();
    const rateInfo = RATE_CARD[sessionForm.engagement_type];
    const hourlyRate = (rateInfo && currentTier) ? (rateInfo[currentTier] || 0) : 0;
    const totalHours = calculateTotalHours(sessionForm);
    return Math.round(hourlyRate * totalHours);
  };

  // 2. Handle Modal Open & Fetch Schedule
  const handleOpenProfile = async (prof) => {
    setSelectedProf(prof);
    setShowProfileModal(true);
    setLoadingFeedback(true);
    try {
      const res = await api.get(`/feedback/expert/${prof.id}`);
      setProfFeedback(res.data?.feedbacks || []);
    } catch (err) {
      console.error("Failed to fetch feedback", err);
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleOpenModal = async (prof, e) => {
    if (e) e.stopPropagation();
    setSelectedProf(prof);
    setShowModal(true);
    setLoadingCalendar(true);
    // Initialize form with professor's domain
    setSessionForm(prev => ({ ...prev, domain: prof.domain || "" }));
    setBusySlots([]);
    setAvailability([]);

    try {
      // Fetch Busy Slots
      const busyRes = await api.get(`/professor/${prof.id}/busy-slots`);
      setBusySlots(busyRes.data);

      let availData = [];
      try {
        // STRATEGY 1: Singular availability (New Endpoint)
        const res = await api.get(`/professor/${prof.id}/availability`);
        availData = res.data;
      } catch (e1) {
        try {
          // STRATEGY 2: Plural availability
          const res = await api.get(`/professors/${prof.id}/availability`);
          availData = res.data;
        } catch (e2) {
          try {
            // STRATEGY 3: Profile Detail
            const profileRes = await api.get(`/professors/${prof.id}`);
            if (profileRes.data && profileRes.data.availability) availData = profileRes.data.availability;
          } catch (e3) {
            console.warn("All strategies failed");
          }
        }
      }

      if (!Array.isArray(availData)) availData = [];
      setAvailability(availData);
    } catch (err) {
      console.error("Calendar fetch error:", err);
    } finally {
      setLoadingCalendar(false);
    }
  };

  // 3. Submit Custom Proposal
  // 3. Submit Custom Proposal
  const handleProposeSubmit = async (e) => {
    e.preventDefault();
    // Validation:
    // For Modular/Visiting: Must accept time slot from calendar (or manual input if we allow)
    // For Adjunct/Fractional: Must have start date, end date, and specific type requirements
    const isLongTerm = ['ADJUNCT', 'FRACTIONAL'].includes(sessionForm.engagement_type);

    if (isLongTerm) {
      if (!sessionForm.event_date || !sessionForm.end_date) {
        setNotification({ message: "Please select both Start Date and End Date for this engagement.", type: 'error' });
        return;
      }

      // Adjunct/Modular needs Start/End Time
      if (['ADJUNCT', 'MODULAR'].includes(sessionForm.engagement_type)) {
        if (!sessionForm.start_time || !sessionForm.end_time) {
          setNotification({ message: "Please specify both Start Time and End Time.", type: 'error' });
          return;
        }
        if (sessionForm.start_time >= sessionForm.end_time) {
          setNotification({ message: "End Time must be after Start Time.", type: 'error' });
          return;
        }
      }

      // Adjunct needs days of week
      if (sessionForm.engagement_type === 'ADJUNCT' && sessionForm.days_of_week.length === 0) {
        setNotification({ message: "Please select at least one day of the week.", type: 'error' });
        return;
      }
    } else {
      // Modular / Visiting
      if (!sessionForm.event_date || !sessionForm.start_time) {
        setNotification({ message: "Please select a specific time slot on the calendar.", type: 'error' });
        return;
      }
    }

    const selectedDateTime = new Date(`${sessionForm.event_date}T${sessionForm.start_time}`);
    if (selectedDateTime < new Date()) {
      setNotification({ message: "You cannot schedule a session in the past. Please select a future time.", type: 'error' });
      return;
    }

    // GLOBAL WINDOW CHECK
    if (selectedProf.window_start_date || selectedProf.window_end_date) {
      const pStartStr = sessionForm.event_date;
      const pEndStr = sessionForm.end_date || sessionForm.event_date;

      if (selectedProf.window_start_date) {
        const wStartStr = selectedProf.window_start_date.split('T')[0];
        if (pStartStr < wStartStr) {
          setNotification({ message: `Professor is not available before ${new Date(selectedProf.window_start_date).toLocaleDateString()}.`, type: 'error' });
          return;
        }
      }
      if (selectedProf.window_end_date) {
        const wEndStr = selectedProf.window_end_date.split('T')[0];
        if (pEndStr > wEndStr) {
          setNotification({ message: `Professor is not available after ${new Date(selectedProf.window_end_date).toLocaleDateString()}. The engagement ends on ${new Date(pEndStr).toLocaleDateString()}.`, type: 'error' });
          return;
        }
      }
    }

    // STRICT AVAILABILITY CHECK
    if (availability.length > 0) {
      const proposedStart = sessionForm.start_time; // HH:MM
      const proposedEnd = sessionForm.end_time;     // HH:MM

      // Helper to check time range overlap
      const isTimeWithin = (slotStart, slotEnd, propStart, propEnd) => {
        return propStart >= slotStart && propEnd <= slotEnd;
      };

      if (sessionForm.engagement_type === 'ADJUNCT') {
        const allDaysCovered = sessionForm.days_of_week.every(day => {
          return availability.some(slot => {
            if (slot.day_of_week === day) {
              return isTimeWithin(slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), proposedStart, proposedEnd);
            }
            return false;
          });
        });

        if (!allDaysCovered) {
          setNotification({
            message: `The professor is not available on some of your selected days at ${proposedStart} - ${proposedEnd}. Please check the green blocks.`,
            type: 'error'
          });
          return;
        }
      }
      else if (sessionForm.engagement_type === 'MODULAR') {
        const startDate = new Date(sessionForm.event_date);
        const endDate = new Date(sessionForm.end_date);
        let failDate = null;

        // Loop through dates
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
          const dateStr = d.toISOString().split('T')[0];

          const hasSlot = availability.some(slot => {
            if (slot.specific_date) {
              const slotDate = slot.specific_date.split('T')[0];
              if (slotDate === dateStr) {
                return isTimeWithin(slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), proposedStart, proposedEnd);
              }
            }
            if (slot.day_of_week === dayName && !slot.specific_date) {
              return isTimeWithin(slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), proposedStart, proposedEnd);
            }
            return false;
          });

          if (!hasSlot) {
            failDate = dateStr;
            break;
          }
        }

        if (failDate) {
          setNotification({
            message: `The professor is not available on ${failDate} at ${proposedStart}. Please check the green blocks.`,
            type: 'error'
          });
          return;
        }
      }
      else if (sessionForm.engagement_type === 'VISITING') {
        const dayName = new Date(sessionForm.event_date).toLocaleDateString('en-US', { weekday: 'long' });
        const dateStr = sessionForm.event_date;

        const hasSlot = availability.some(slot => {
          if (slot.specific_date) {
            const slotDate = slot.specific_date.split('T')[0];
            if (slotDate === dateStr) {
              return isTimeWithin(slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), proposedStart, proposedEnd);
            }
          }
          if (slot.day_of_week === dayName && !slot.specific_date) {
            return isTimeWithin(slot.start_time.slice(0, 5), slot.end_time.slice(0, 5), proposedStart, proposedEnd);
          }
          return false;
        });

        if (!hasSlot) {
          setNotification({
            message: `The professor is not available on ${sessionForm.event_date} at ${proposedStart}. Please check the green blocks.`,
            type: 'error'
          });
          return;
        }
      }
    }

    try {
      // Calculate budget and total hours for payload
      const totalHours = calculateTotalHours(sessionForm);
      const budget = calculateBudget();

      const payload = {
        title: sessionForm.title,
        description: sessionForm.description,
        required_domain: sessionForm.domain,
        engagement_type: sessionForm.engagement_type,
        delivery_mode: sessionForm.delivery_mode,
        event_date: sessionForm.event_date || null,
        end_date: sessionForm.end_date || null,
        start_time: sessionForm.start_time || null,
        end_time: sessionForm.end_time || null,
        days_of_week: sessionForm.days_of_week,
        weekly_hours: sessionForm.weekly_hours,
        duration_value: sessionForm.duration_value,
        duration_unit: "Hours",
        budget: budget,
        total_estimated_hours: totalHours
      };

      await api.post(`/university/propose-custom-session/${selectedProf.id}`, payload);
      setNotification({
        message: `Proposal for "${sessionForm.title}" sent to ${selectedProf.full_name}!`,
        type: 'success'
      });
      setShowModal(false);
      // Reset form
      setSessionForm({
        title: '', description: '', domain: '', engagement_type: 'MODULAR', delivery_mode: 'Remote',
        event_date: '', start_time: '', end_time: '', duration_value: 1,
        days_of_week: [], end_date: '', weekly_hours: 0
      });
    } catch (err) {
      console.error(err);
      // Show specific error message from backend if available
      const errorMessage = err.response?.data?.detail || "Failed to send proposal. This slot might have just been taken.";
      setNotification({ message: errorMessage, type: 'error' });
    }
  };

  const handleToggleFilter = (category, value) => {
    const currentList = [...filters[category]];
    const newList = currentList.includes(value)
      ? currentList.filter(item => item !== value)
      : [...currentList, value];
    setFilters({ ...filters, [category]: newList });
  };

  // 4. Calendar Event Memo
  const calendarEvents = useMemo(() => {
    const events = [];

    // 1. Availability constraints (Background Events - Green)
    availability.forEach(slot => {
      const getDayNumber = (day) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days.indexOf(day);
      };

      if (slot.day_of_week && !slot.specific_date) {
        events.push({
          groupId: 'availableForSession',
          daysOfWeek: [getDayNumber(slot.day_of_week)],
          startTime: slot.start_time,
          endTime: slot.end_time,
          display: 'background',
          color: '#58e78aff' // Darker green
        });
      } else if (slot.specific_date) {
        let dateOnly = slot.specific_date;
        if (dateOnly.includes('T')) dateOnly = dateOnly.split('T')[0];

        events.push({
          groupId: 'availableForSession',
          start: `${dateOnly}T${slot.start_time}`,
          end: `${dateOnly}T${slot.end_time}`,
          display: 'background',
          color: '#6dea99' // Darker green
        });
      }
    });

    // 2. Add busy slots as blocked events (Grey)
    const formattedBusy = busySlots.map(slot => ({
      ...slot,
      title: slot.title || 'Occupied',
      display: 'block',
      backgroundColor: 'var(--border)', // Light Grey
      borderColor: '#cbd5e0',
      textColor: '#718096',
      editable: false,
      overlap: false,
    }));
    events.push(...formattedBusy);

    // 3. Current Selection (Blue)
    // 3. Current Selection (Blue)
    if (sessionForm.start_time && sessionForm.end_time) {
      // ADJUNCT: Show recurring weekly slots based on selection
      if (sessionForm.engagement_type === 'ADJUNCT' && sessionForm.days_of_week.length > 0) {
        const daysMap = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };
        const selectedDays = sessionForm.days_of_week.map(d => daysMap[d]);

        events.push({
          id: 'university-selection-recurring',
          title: 'Your Proposal',
          daysOfWeek: selectedDays,
          startTime: sessionForm.start_time,
          endTime: sessionForm.end_time,
          backgroundColor: '#4f46e5',
          borderColor: '#4338ca',
          textColor: '#ffffff'
        });
      }
      // MODULAR: Show recurring daily slots across the date range
      else if (sessionForm.engagement_type === 'MODULAR' && sessionForm.event_date && sessionForm.end_date) {
        // Calculate inclusive end date (end_date + 1 day) for FullCalendar
        const endDate = new Date(sessionForm.end_date);
        endDate.setDate(endDate.getDate() + 1);
        const endRecurStr = endDate.toISOString().split('T')[0];

        events.push({
          id: 'university-selection-modular',
          title: 'Proposed Module',
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // Show on all days
          startTime: sessionForm.start_time,
          endTime: sessionForm.end_time,
          startRecur: sessionForm.event_date,
          endRecur: endRecurStr,
          backgroundColor: '#4f46e5',
          borderColor: '#4338ca',
          textColor: '#ffffff'
        });
      }
      // VISITING / FRACTIONAL: Show on Start Date
      else if (sessionForm.event_date) {
        events.push({
          id: 'university-selection',
          title: 'Proposed Start',
          start: `${sessionForm.event_date}T${sessionForm.start_time}`,
          end: `${sessionForm.event_date}T${sessionForm.end_time}`,
          backgroundColor: '#4f46e5',
          borderColor: '#4338ca',
          textColor: '#ffffff'
        });
      }
    }
    return events;
  }, [busySlots, sessionForm, availability]);

  return (
    <div className="unidis-container">
      {/* FILTER SIDEBAR */}
      <aside className="unidis-filters-sidebar">
        <div className="unidis-filter-header">
          <h4><Filter size={16} /> Filters</h4>
          <button onClick={() => setFilters({ nameSearch: '', domainSearch: '', roleFilter: 'ALL', skills: [], domain: [], seniority: [], minCredibility: 0 })} className="unidis-reset-link">Reset All</button>
        </div>

        <div className="unidis-filter-group">
          <h5>ROLE</h5>
          <select 
            value={filters.roleFilter || 'ALL'}
            onChange={(e) => setFilters({ ...filters, roleFilter: e.target.value })}
            className="unidis-filter-select"
          >
            <option value="ALL">All Roles</option>
            <option value="POP">Professor of Practice (10+ yrs)</option>
            <option value="INSTRUCTOR">Instructor (&lt; 10 yrs)</option>
          </select>
        </div>

        <div className="unidis-filter-group">
          <h5>INDUSTRY DOMAIN</h5>
          <input type="text" placeholder="Search domains..." className="unidis-filter-search" value={filters.domainSearch} onChange={(e) => setFilters({ ...filters, domainSearch: e.target.value })} />
          <div className="unidis-checkbox-list">
            {availableDomains
              .filter(d => {
                if (!filters.domainSearch) return true;
                return d.toLowerCase().includes(filters.domainSearch.toLowerCase());
              })
              .slice(0, filters.domainSearch ? undefined : 5)
              .map(d => (
                <label key={d} className="unidis-checkbox-label">
                  <input
                    type="checkbox"
                    checked={filters.domain.includes(d)}
                    onChange={() => handleToggleFilter('domain', d)}
                  />
                  <span className="unidis-custom-checkbox"></span>
                  {d}
                </label>
              ))}
          </div>
        </div>

        <div className="unidis-filter-group">
          <h5>EXPERTISE</h5>
          <SkillsInput
            selectedSkills={filters.skills}
            onSkillsChange={(newSkills) => setFilters({ ...filters, skills: newSkills })}
          />
          <div className="unidis-checkbox-list unidis-mt-10">
            {commonSkills.map(s => (
              <label key={s} className="unidis-checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.skills.includes(s)}
                  onChange={() => handleToggleFilter('skills', s)}
                />
                <span className="unidis-custom-checkbox"></span>
                {s}
              </label>
            ))}
          </div>
        </div>

        <div className="unidis-filter-group">
          <h5>MIN. YEARS OF EXPERIENCE</h5>
          <div className="credibility-slider-container">
            <input
              type="range"
              min="0"
              max="40"
              value={filters.minExperience}
              onChange={(e) => setFilters({ ...filters, minExperience: parseInt(e.target.value) })}
              className="stu-range-slider"
              style={{ background: `linear-gradient(to right, var(--cyan) 0%, var(--cyan) ${(filters.minExperience / 40) * 100}%, var(--border) ${(filters.minExperience / 40) * 100}%, var(--border) 100%)` }}
            />
            <div className="slider-value-label">{filters.minExperience}+ Years</div>
          </div>
        </div>

        <div className="unidis-filter-group">
          <h5>CREDIBILITY SCORE</h5>
          <div className="credibility-slider-container">
            <input
              type="range"
              min="0"
              max="100"
              value={filters.minCredibility}
              onChange={(e) => setFilters({ ...filters, minCredibility: parseInt(e.target.value) })}
              className="stu-range-slider"
              style={{ background: `linear-gradient(to right, #008CBA 0%, #008CBA ${filters.minCredibility}%, var(--border) ${filters.minCredibility}%, var(--border) 100%)` }}
            />
            <div className="slider-value-label">Minimum: {filters.minCredibility}</div>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="unidis-main-content">
        <div className="unidis-list-header">
          <h1>Discover Professors</h1>
          <p>Showing <strong>{professors.filter(p => {
            if (!filters.nameSearch) return true;
            return p.full_name?.toLowerCase().includes(filters.nameSearch.toLowerCase());
          }).length}</strong> verified experts matching your criteria</p>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative', maxWidth: '600px' }}>
            <SearchIcon size={20} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)' }} />
            <input
              type="text"
              placeholder="Search professors by name..."
              value={filters.nameSearch}
              onChange={(e) => setFilters({ ...filters, nameSearch: e.target.value })}
              className="unidis-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="unidis-loader-wrapper">
            <div className="unidis-spinner"></div>
            <p>Scanning verified expertise...</p>
          </div>
        ) : (
          <div className="unidis-prof-grid">
            {professors
              .filter(p => {
                if (filters.roleFilter === 'POP' && (p.years_of_experience || 0) < 10) return false;
                if (filters.roleFilter === 'INSTRUCTOR' && (p.years_of_experience || 0) >= 10) return false;
                if (filters.nameSearch && !p.full_name?.toLowerCase().includes(filters.nameSearch.toLowerCase())) return false;
                return true;
              })
              .map(p => (
                <div key={p.id} className="unidis-professor-card hoverable-card" onClick={() => handleOpenProfile(p)}>
                  <div className="unidis-card-left">
                    <div className="unidis-prof-avatar">
                      {p.profile_photo_path ? (
                        <img src={p.profile_photo_path.startsWith('http') ? p.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${p.profile_photo_path.startsWith('/') ? '' : '/'}${p.profile_photo_path}`} alt={p.full_name} />
                      ) : (
                        p.full_name ? p.full_name[0] : "P"
                      )}
                    </div>
                  </div>

                  <div className="unidis-card-center">
                    <div className="unidis-prof-name-row">
                      <h4>{p.full_name}</h4>
                      <span className={`seniority-badge ${p.years_of_experience >= 10 ? 'pop' : 'instructor'}`}>
                          {p.years_of_experience >= 10 ? 'Professor of Practice' : 'Instructor'}
                      </span>
                      <span className="unidis-online-badge">ONLINE</span>
                    </div>
                    <p className="unidis-prof-headline">
                      <Briefcase size={12} /> {p.job_title} @ {p.company}
                    </p>

                    <div className="unidis-badge-row">
                      <span className={`unidis-pill unidis-domain-pill`}><strong>Domain:</strong> {p.domain || "AI"}</span>
                      {/* <span className="unidis-pill unidis-seniority-pill"><strong>Exp:</strong> {p.years_of_experience} Years</span>
                      {p.highest_education && (
                        <span className="unidis-pill unidis-education-pill" style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' }}>
                          <strong>Education:</strong> {p.highest_education}
                        </span>
                      )} */}
                    </div>

                    {p.latest_feedbacks && p.latest_feedbacks.length > 0 && (
                      <div className="unidis-feedback-preview-container">
                        {p.latest_feedbacks.map((fb, idx) => (
                          <div key={idx} className="unidis-feedback-pill-sample">
                            <div className="unidis-feedback-meta">
                              <span className="stars">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span>
                              Latest Feedback {p.latest_feedbacks.length > 1 ? `#${idx + 1}` : ''}
                            </div>
                            {fb.comment && (
                              <div className="unidis-feedback-comment">
                                "{fb.comment.length > 60 ? fb.comment.substring(0, 60) + '...' : fb.comment}"
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* <div className="unidis-tags" style={{ alignItems: 'center', marginTop: '12px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)' }}>Skills:</span>
                      {p.skills?.map(skill => (
                        <span key={skill} className="unidis-tag-item">{skill}</span>
                      ))}
                    </div> */}
                    {p.linkedin_url && (
                      <a href={/^https?:\/\//i.test(p.linkedin_url) ? p.linkedin_url : `https://${p.linkedin_url}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0077b5', textDecoration: 'none', marginTop: '12px', fontWeight: 'bold' }}>
                        <Linkedin size={14} /> LinkedIn Profile
                      </a>
                    )}

                  </div>

                  <div className="unidis-card-right" style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                    <div className="unidis-cred-box" style={{ padding: '0 0 8px 0', marginBottom: '4px' }}>
                      <span className="unidis-cred-score" style={{ fontSize: '2rem' }}>{p.credibility || 92}</span>
                      <span className="unidis-cred-label" style={{ fontSize: '10px' }}>Score</span>
                    </div>

                    {/* <div className="stu-prof-pricing-modern" style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(p.university_hourly_rate && p.university_hourly_rate > 0) ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fdf4ff', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f0abfc' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#86198f', fontWeight: '600' }}>
                            <Briefcase size={14} /> University Rate
                          </div>
                          <div style={{ fontSize: '15px', fontWeight: '800', color: '#701a75' }}>
                            ₹{p.university_hourly_rate}<span style={{ fontSize: '11px', color: '#a21caf', fontWeight: '500' }}>/hr</span>
                          </div>
                        </div>
                      ) : (p.individual_session_price && p.individual_session_price > 0) ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--engine-black)', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600' }}>
                            <User size={14} /> 1:1 Base Rate
                          </div>
                          <div style={{ fontSize: '15px', fontWeight: '800', color: 'var(--proto-white)' }}>
                            ₹{p.individual_session_price}<span style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: '500' }}>/hr</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '8px', background: 'var(--engine-black)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Contact for Pricing</div>
                        </div>
                      )}
                    </div> */}

                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button className="unidis-view-profile-btn secondary" onClick={(e) => { e.stopPropagation(); navigate('/chat', { state: { startChatWith: { id: p.id, name: p.full_name, role: 'pop' } } }) }} style={{ flex: 1, padding: '8px' }}>
                        <MessageSquare size={16} />
                      </button>
                      <button className="unidis-view-profile-btn" onClick={(e) => handleOpenModal(p, e)} style={{ flex: 4, padding: '8px' }}>
                        <Zap size={14} /> Propose Session
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {showProfileModal && selectedProf && (
        <div className="stu-modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="stu-modal-content large-propose pop-fade-up" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', display: 'flex', flexDirection: 'column' }}>
            <div className="stu-modal-header" style={{ paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
              <div className="modal-title-context" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div className="stu-prof-avatar" style={{ width: '64px', height: '64px', fontSize: '24px', flexShrink: 0 }}>
                  {selectedProf.profile_photo_path ? <img src={selectedProf.profile_photo_path.startsWith('http') ? selectedProf.profile_photo_path : `${process.env.REACT_APP_API_URL || ''}${selectedProf.profile_photo_path.startsWith('/') ? '' : '/'}${selectedProf.profile_photo_path}`} alt={selectedProf.full_name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : (selectedProf.full_name ? selectedProf.full_name[0] : 'P')}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--proto-white)' }}>{selectedProf.full_name}</h2>
                  <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '0.95rem' }}>{selectedProf.job_title} @ {selectedProf.company}</p>
                </div>
              </div>
              <button className="close-btn-large" onClick={() => setShowProfileModal(false)}><X size={28} /></button>
            </div>

            <div className="large-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px', overflowY: 'auto', maxHeight: '70vh' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', background: 'var(--engine-black)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Domain</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{selectedProf.domain || "General"}</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Experience</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{selectedProf.years_of_experience} Years</span></div>
                <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Credibility</strong> <span style={{ color: '#10b981', fontWeight: '700' }}>{selectedProf.credibility || 92}/100</span></div>
                {selectedProf.highest_education && (
                  <div><strong style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Education</strong> <span style={{ color: 'var(--proto-white)', fontWeight: '600' }}>{selectedProf.highest_education}</span></div>
                )}
              </div>

              {selectedProf.bio && (
                <div>
                  <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>About</h4>
                  <div style={{ background: 'var(--graphite-dark)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)', lineHeight: '1.6', color: 'var(--text-muted)', fontSize: '14.5px', whiteSpace: 'pre-line' }}>
                    {selectedProf.bio}
                  </div>
                </div>
              )}

              {selectedProf.skills && selectedProf.skills.length > 0 && (
                <div>
                  <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Expertise & Skills</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedProf.skills.map(skill => (
                      <span key={skill} style={{ padding: '6px 12px', background: 'var(--border)', color: 'var(--proto-white)', borderRadius: '20px', fontSize: '13px', fontWeight: '500', border: '1px solid var(--border)' }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 style={{ color: 'var(--proto-white)', marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Latest Feedback
                  {loadingFeedback && <span className="stu-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>}
                </h4>
                {!loadingFeedback && profFeedback.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', background: 'var(--engine-black)', borderRadius: '8px', color: 'var(--text-faint)', fontStyle: 'italic', border: '1px solid var(--border)' }}>
                    No feedback received yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {profFeedback.slice(0, 3).map(fb => (
                      <div key={fb.id} style={{ padding: '16px', background: 'var(--graphite-dark)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <strong style={{ fontSize: '14px', color: 'var(--proto-white)' }}>{fb.student_name || "Anonymous"}</strong>
                          <span style={{ display: 'flex', gap: '2px', color: '#fbbf24' }}>
                            {Array.from({ length: 5 }).map((_, i) => (
                              <span key={i} style={{ opacity: i < fb.quality_rating ? 1 : 0.3 }}>★</span>
                            ))}
                          </span>
                        </div>
                        {fb.comments && <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.5', fontStyle: 'italic' }}>"{fb.comments}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 'auto', paddingTop: '24px', display: 'flex', gap: '16px', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
                <button
                  className="stu-btn-outline"
                  onClick={() => setShowProfileModal(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: '1.5px solid var(--border)',
                    background: 'var(--graphite-dark)',
                    color: 'var(--text-muted)',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--text-faint)'; e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}
                >
                  Close Summary
                </button>
                <button
                  className="stu-btn-primary"
                  onClick={() => { setShowProfileModal(false); setTimeout(() => handleOpenModal(selectedProf), 100); }}
                  style={{
                    padding: '12px 28px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(79, 70, 229, 0.35)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.25)'; }}
                >
                  <Zap size={18} /> Propose Session
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* PROPOSAL MODAL */}
      {showModal && selectedProf && (
        <div className="stu-modal-overlay"> {/* Using shared overlay class for consistency */}
          <div className="unidis-modal-card pop-fade-up">
            <div className="unidis-modal-header">
              <div className="unidis-modal-title-context">
                <h2>Personal University Proposal</h2>
                <p>Requesting: Prof. {selectedProf.full_name}</p>
              </div>
              <button className="unidis-close-x" onClick={() => setShowModal(false)}><X size={24} /></button>
            </div>

            <form onSubmit={handleProposeSubmit} className="unidis-modal-content-grid">
              <div className="unidis-modal-form-side">
                <div className="unidis-input-group">
                  <label>Session Title / Topic</label>
                  <input
                    required
                    placeholder="e.g. Executive Guest Lecture on Fintech"
                    value={sessionForm.title}
                    onChange={e => setSessionForm({ ...sessionForm, title: e.target.value })}
                  />
                </div>

                <div className="unidis-input-group">
                  <label>Session Details & Schedule Details</label>
                  <textarea
                    rows={3}
                    placeholder="Provide details about what you expect in the session, specific schedule notes, time zone, etc."
                    value={sessionForm.description}
                    onChange={e => setSessionForm({ ...sessionForm, description: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: '14px',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  {/* Scheduling Constraints Info */}
                  {(selectedProf.weekly_quota_hours || selectedProf.window_start_date || selectedProf.window_end_date) && (
                    <div style={{ marginTop: '12px', padding: '10px', background: 'var(--engine-black)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Availability Constraints</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {selectedProf.weekly_quota_hours && (
                          <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '600' }}>• Weekly Quota: {selectedProf.weekly_quota_hours} hrs</div>
                        )}
                        {(selectedProf.window_start_date || selectedProf.window_end_date) && (
                          <div style={{ fontSize: '12px', color: '#b91c1c', fontWeight: '600' }}>
                            • Active Window: {selectedProf.window_start_date ? new Date(selectedProf.window_start_date).toLocaleDateString() : 'Start'} to {selectedProf.window_end_date ? new Date(selectedProf.window_end_date).toLocaleDateString() : 'End'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="unidis-grid-half">
                  <div className="unidis-input-group">
                    <label>Engagement Type</label>
                    <select value={sessionForm.engagement_type} onChange={e => setSessionForm({ ...sessionForm, engagement_type: e.target.value })}>
                      <option value="MODULAR">Modular</option>
                      <option value="VISITING">Visiting Faculty</option>
                      <option value="ADJUNCT">Adjunct Professor</option>
                      <option value="FRACTIONAL">Fractional/Part-time</option>
                    </select>
                  </div>
                  <div className="unidis-input-group">
                    <label>Delivery Mode</label>
                    <select value={sessionForm.delivery_mode} onChange={e => setSessionForm({ ...sessionForm, delivery_mode: e.target.value })}>
                      <option value="Remote">Remote</option>
                      <option value="In-Person">In-Person</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                  <div className="unidis-input-group">
                    <label>Relevant Domain</label>
                    <input required value={sessionForm.domain} onChange={e => setSessionForm({ ...sessionForm, domain: e.target.value })} />
                  </div>
                </div>

                {/* MANUAL INPUTS FOR LONG-TERM (Adjunct/Fractional/Modular) */}
                {['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(sessionForm.engagement_type) && (
                  <>
                    <div className="unidis-grid-half">
                      <div className="unidis-input-group">
                        <label>Start Date</label>
                        <input
                          type="date"
                          required
                          value={sessionForm.event_date}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={e => setSessionForm({ ...sessionForm, event_date: e.target.value })}
                        />
                      </div>
                      <div className="unidis-input-group">
                        <label>End Date</label>
                        <input
                          type="date"
                          required
                          value={sessionForm.end_date}
                          min={sessionForm.event_date || new Date().toISOString().split('T')[0]}
                          onChange={e => setSessionForm({ ...sessionForm, end_date: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* ADJUNCT & MODULAR: Need Time & Duration */}
                    {(sessionForm.engagement_type === 'ADJUNCT' || sessionForm.engagement_type === 'MODULAR') && (
                      <div className="unidis-grid-half">
                        <div className="unidis-input-group">
                          <label>Recurring Start Time</label>
                          <input
                            type="time"
                            required
                            value={sessionForm.start_time}
                            onChange={e => setSessionForm({ ...sessionForm, start_time: e.target.value })}
                          />
                        </div>
                        <div className="unidis-input-group">
                          <label>Recurring End Time</label>
                          <input
                            type="time"
                            required
                            value={sessionForm.end_time}
                            onChange={e => setSessionForm({ ...sessionForm, end_time: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}


                {/* ADJUNCT: Days of Week Selection */}
                {sessionForm.engagement_type === 'ADJUNCT' && (
                  <div className="unidis-input-group">
                    <label>Required Days of Week</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', background: 'var(--border)', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={sessionForm.days_of_week.includes(day)}
                            onChange={e => {
                              const newDays = e.target.checked
                                ? [...sessionForm.days_of_week, day]
                                : sessionForm.days_of_week.filter(d => d !== day);
                              setSessionForm({ ...sessionForm, days_of_week: newDays });
                            }}
                          />
                          {day.substring(0, 3)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* FRACTIONAL: Weekly Hours */}
                {sessionForm.engagement_type === 'FRACTIONAL' && (
                  <div className="unidis-input-group">
                    <label>Weekly Hours Quota</label>
                    <input
                      type="number"
                      min="1"
                      value={sessionForm.weekly_hours}
                      onChange={e => setSessionForm({ ...sessionForm, weekly_hours: parseInt(e.target.value) })}
                      placeholder="e.g. 10 hours/week"
                    />
                  </div>
                )}

                {/* VISITING: Calendar Selection Info */}
                {!['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(sessionForm.engagement_type) && sessionForm.event_date && (
                  <div className="unidis-selection-pill">
                    <Clock size={16} />
                    <span>
                      <strong>Slot Selected:</strong> {sessionForm.event_date} at {sessionForm.start_time} ({sessionForm.duration_value} hr)
                    </span>
                  </div>
                )}

                {/* Budget Calculation Display */}
                <div style={{ marginTop: '16px', padding: '12px', background: 'var(--engine-black)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-muted)' }}>
                      Standardized Rate ({uniProfile?.tier || 'Unclassified'})
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--proto-white)' }}>
                      ₹{(() => {
                        if (!uniProfile?.tier) return 0;
                        const tierKey = uniProfile.tier.trim();
                        const rate = RATE_CARD[sessionForm.engagement_type]?.[tierKey];
                        return rate || 0;
                      })()}/hr
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Estimated Budget</span>
                    <span style={{ fontSize: '16px', fontWeight: '800', color: '#2563eb' }}>
                      ₹{calculateBudget().toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>Total Estimated Hours</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
                      {calculateTotalHours(sessionForm)} hrs
                    </span>
                  </div>
                </div>

                <button type="submit" className="unidis-submit-proposal-btn">
                  <Zap size={16} /> Send Personal Proposal
                </button>
              </div>

              <div className="unidis-modal-calendar-side">
                <label className="unidis-calendar-label">
                  {['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(sessionForm.engagement_type)
                    ? "Professor's Availability (Reference Only)"
                    : "Select Availability from Professor's Schedule"}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontSize: '13px', color: '#166534' }}>
                  <span style={{ width: '12px', height: '12px', backgroundColor: '#6dea99ff', border: '1px solid #bbf7d0', borderRadius: '3px' }}></span>
                  <span>Available slots are highlighted in green</span>
                </div>
                {loadingCalendar ? (
                  <div className="unidis-calendar-loader">Syncing Professor's availability...</div>
                ) : (
                  <div className="unidis-calendar-wrapper">
                    {availability.length === 0 && (
                      <div style={{ marginBottom: '10px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                        * No specific availability set (ID: {selectedProf?.id || selectedProf?.user_id}). Viewing open schedule.
                      </div>
                    )}
                    <FullCalendar
                      plugins={[timeGridPlugin, interactionPlugin]}
                      initialView="timeGridWeek"
                      locale={enGbLocale}
                      allDaySlot={false}
                      height="480px"
                      slotMinTime="08:00:00"
                      slotMaxTime="21:00:00"
                      selectable={!['ADJUNCT', 'FRACTIONAL', 'MODULAR'].includes(sessionForm.engagement_type)}
                      selectOverlap={(event) => event.display === 'background'}
                      selectConstraint={availability.length > 0 ? "availableForSession" : undefined}
                      eventOverlap={false}
                      unselectAuto={false}
                      selectAllow={(selectInfo) => {
                        return selectInfo.start >= new Date();
                      }}
                      events={calendarEvents}
                      eventClick={(info) => {
                        // If user clicks their own selection, clear it
                        if (info.event.id === 'university-selection') {
                          setSessionForm({
                            ...sessionForm,
                            event_date: '',
                            start_time: '',
                            end_time: '',
                            duration_value: 1,
                            engagement_type: sessionForm.engagement_type
                          });
                          info.view.calendar.unselect();
                        }
                      }}
                      select={(info) => {
                        const diffMs = info.end - info.start;
                        const diffHrs = Math.round(diffMs / (1000 * 60 * 60));
                        setSessionForm({
                          ...sessionForm,
                          event_date: info.startStr.split('T')[0],
                          start_time: info.start.toTimeString().substring(0, 5),
                          end_time: info.end.toTimeString().substring(0, 5),
                          duration_value: diffHrs || 1
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Notification Modal */}
      <NotificationModal
        message={notification?.message}
        type={notification?.type}
        onClose={() => setNotification(null)}
      />
    </div>
  );
}

export default DiscoverProfessorsUni;
